import type { FormEvent } from 'react';
import React, { useId, useRef, useState } from 'react';
import { HBox } from '../layout';
import { Button, Icon } from '../basic';
import {
  Breakpoints,
  intl,
  intlFmtTemplateString,
  useBreakpoint
} from '../../lib';

/**
 * Props for a {@link ChatInput} component.
 */
export interface ChatInputProps {
  /**
   * Handler for when chat message is submitted.
   * @param message message typed in the text input.
   */
  onMessageSubmit: (message: string) => void;
  showAttachments: boolean;
  showEmojis: boolean;
  disabled?: boolean;
  maxTextLength?: number;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
  'data-testid'?: string;
}

/**
 * Basic Chat input element.
 *
 * It consists of {@link TextInput} and {@link Button}. When submit is triggered, provided  onMessageSubmit function is fired.

 * @constructor
 */
export function ChatInput({
  onMessageSubmit,
  showAttachments = true,
  showEmojis = true,
  disabled = false,
  maxTextLength = 10000,
  textareaRef: forwardedTextareaRef,
  ...props
}: Readonly<ChatInputProps>): React.JSX.Element {
  const [message, setMessage] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [classes, setClasses] = useState(['ehs-chat-input']);
  const [errorMessage, setErrorMessage] = useState('');
  const errorId = useId();
  if (errorMessage) {
    classes.push('error');
  }

  const setTextareaRef = (element: HTMLTextAreaElement | null) => {
    textareaRef.current = element;
    if (forwardedTextareaRef && element) {
      forwardedTextareaRef.current = element;
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onMessageSubmit(message);
    setMessage('');
    setClasses(classes.filter(c => c !== 'extended'));
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.focus();
    }
  };

  const smallScreen = useBreakpoint(Breakpoints.SMALL);
  const Buttons = () => {
    const innerButtons = (
      <>
        {showAttachments && (
          <Button
            variant="tertiary"
            className="attachment"
            text={intl('share.lbAttachment')}
            icon="attachment"
            scale="small"
            data-testid={testId && testId + '-attachment-button'}
          />
        )}
        {showEmojis && (
          <Button
            variant="tertiary"
            className="emoji"
            text={intl('common.addEmoji')}
            icon="emoji"
            scale="small"
            data-testid={testId && testId + '-emoji-button'}
          />
        )}
        {(showEmojis || showAttachments) && (
          <div className="button-divider"></div>
        )}
        <Button
          className="submit"
          icon="send_message"
          text={intl('common.send.message')}
          variant="tertiary"
          type="submit"
          disabled={sendMessageForbidden}
          scale="small"
          data-testid={testId && testId + '-submit-button'}
        />
      </>
    );
    if (smallScreen) {
      return innerButtons;
    } else {
      return <HBox className="button-container">{innerButtons}</HBox>;
    }
  };
  const testId = props['data-testid']?.trim();

  const sendMessageForbidden = message.trim() === '' || errorMessage != '';
  return (
    <form
      {...props}
      className={classes.join(' ')}
      onSubmit={handleSubmit}
      data-testid={testId}
    >
      <HBox align="stretch" className="textfield-container">
        <label htmlFor="chat-message" className="sr-only">
          {intl('common.chatmessage')}
        </label>
        <textarea
          id="chat-message"
          data-testid={testId + '-message-input'}
          ref={setTextareaRef}
          className="textfield-input"
          disabled={disabled}
          rows={1}
          placeholder={intl('common.messaging.input.label')}
          value={message}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (!sendMessageForbidden) {
                event.currentTarget.form?.requestSubmit();
              }
            }
          }}
          onChange={value => {
            if (value.target.value.length > maxTextLength) {
              setErrorMessage(
                intlFmtTemplateString('react.field.string.max', {
                  max: maxTextLength.toString()
                })
              );
            } else {
              setErrorMessage('');
              setClasses(classes.filter(c => c !== 'error'));
              setMessage(value.target.value);
            }
          }}
          onInput={e => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = `${target.scrollHeight}px`;
            if (target.scrollHeight > 43 && classes.length === 1) {
              setClasses([...classes, 'extended']);
            }
            if (target.scrollHeight <= 43 && classes.length === 2) {
              setClasses(classes.filter(c => c !== 'extended'));
            }
          }}
          aria-describedby={errorMessage ? errorId : undefined}
        />
        <Buttons />
      </HBox>
      {errorMessage && (
        <HBox id={errorId} className="error-message">
          <Icon iconName="shui_error" aria-label={undefined} />
          {errorMessage}
        </HBox>
      )}
    </form>
  );
}
